import React, { useEffect } from 'react';
import * as mobxReact from 'mobx-react';
import * as IoC from 'typescript-ioc';
import { ManageEventNavigation } from '../shared/navigation/navigation';
import ArtifactsViewModel from './artifacts.viewmodel';
import { ITheadSort, ListLayout } from '../../../global/tables/table-list-layout';
import { IMeetingArtifactDTO } from '../../../../api/artifacts.dao';
import TableLoadingRow from '../../../global/tables/table-loading-row';
import MeetingArtifactListItem from './_meeting-artifact-list-item/meeting-artifact-list-item';

import './artifacts.scss';
import { DateTimeFormatter } from '../../../../infrastructure/implementations/date-formatter';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../api/roles.dao';
import DefaultButton from '../../../global/default-button/default-button';
import RequestArtifactsPopover from './request-artifacts-popover/request-artifacts-popover';
import PublishArtifactPopover from './publish-artifact-popover/publish-artifact-popover';
import { DATE_AND_TIME_TEXT, TOPIC_TEXT } from '../../../../constants/common-labels.constants';
import KnovioPreviewLinkPopover from '../shared/knovio-preview-link-popover/knovio-preview-link-popover';

interface IProps {
    vm: ArtifactsViewModel;
}

const dateTimeFormatter = IoC.Container.get<DateTimeFormatter>(DateTimeFormatter);

const ArtifactsPresentation = mobxReact.observer((props: IProps) => {
    const {
        vm,
    } = props;
    const {
        intl,
        event,
        loading,
        meetingsArtifacts,
        openRequestArtifactsModal,
        requestArtifactsPopoverVM,
        publishArtifactPopoverVM
    } = vm;

    const downloadArtifactPermission = hasPermission(Permission.DOWNLOAD_ARTIFACT);
    const deleteArtifactPermission = hasPermission(Permission.DELETE_ARTIFACT);
    const requestArtifactPermission = hasPermission(Permission.REQUEST_ARTIFACTS) || hasPermission(Permission.REQUEST_AI_TRANSCRIPT);
    const requestPassportReplayPermission = hasPermission(Permission.REQUEST_PASSPORT_REPLAY);
    const deleteArtifactDownloadLinkPermission = hasPermission(Permission.DELETE_ARTIFACT_DOWNLOAD_LINK);

    const isEmpty = !meetingsArtifacts.length;
    const isLoading = loading;
    const artifactTHeads: ITheadSort<string>[] = [
        {
            title: intl.formatMessage({
                id: 'oe.common.artifacts',
                defaultMessage: 'Artifacts',
                description: 'Label text',
            }),
            width: '150px'
        },
        {
            title: intl.formatMessage({
                id: 'oe.common.status',
                defaultMessage: 'Status',
                description: 'Label text',
            })
        },
        {
            title: intl.formatMessage({
                id: 'oe.common.link',
                defaultMessage: 'Link',
                description: 'Label text',
            })
        },
        {
            title: intl.formatMessage({
                id: 'oe.common.notes',
                defaultMessage: 'Notes',
                description: 'Label text',
            },
            {
                prefix: ''
            })
        },
        {
            width: '150px'
        }
    ];
    const meetingTHeads: ITheadSort<string>[] = [
        {
            title: intl.formatMessage(DATE_AND_TIME_TEXT)
        },
        {
            title: intl.formatMessage(TOPIC_TEXT)
        },
        {
            title: intl.formatMessage({
                id: 'oe.common.presenters',
                defaultMessage: 'Presenters',
                description: 'Label text',
            })
        },
        {
            title: intl.formatMessage({
                id: 'oe.common.meetingType',
                defaultMessage: 'Meeting Type',
                description: 'Label text',
            })
        }
    ];
    const theads: ITheadSort<string>[] = [
        ...meetingTHeads,
        ...artifactTHeads
    ];
    const colspan = theads.length - artifactTHeads.length;

    return (
        <div>
            <section className='horizontal-row horizontal-row--with-bottom-margin event-tabs'>
                <ManageEventNavigation />
                <div className='horizontal-row__align-to-row-end'>
                    <div className='buttons-block'>
                        {
                            (requestArtifactPermission || requestPassportReplayPermission) &&
                            <DefaultButton
                                modifier={['fat', 'confirm-safe']}
                                text={intl.formatMessage({
                                    id: 'oe.artifacts.requestArtifacts',
                                    defaultMessage: 'Request Artifacts',
                                    description: 'Label text',
                                })}
                                onClick={() => openRequestArtifactsModal()}
                                dataTestId='request-artifacts-modal'
                            />
                        }
                    </div>
                </div>
            </section>
            <section className='artifacts-list fixed-list-layout'>
                <div>
                    <ListLayout
                        thead={theads}
                        sortOptions={{
                            theads: theads,
                        }}
                        isDisabled={isEmpty}
                    >
                        {isLoading && <TableLoadingRow colSpan={theads.length} />}
                        {(!isLoading && !isEmpty)
                            && meetingsArtifacts.map((meeting: IMeetingArtifactDTO, index: number) => {
                                return <MeetingArtifactListItem
                                    downloadArtifactPermission={downloadArtifactPermission}
                                    deleteArtifactPermission={deleteArtifactPermission}
                                    deleteArtifactDownloadLinkPermission={deleteArtifactDownloadLinkPermission}
                                    key={meeting.meetingId}
                                    meeting={meeting}
                                    eventTimeZone={event.timeZone}
                                    intl={intl}
                                    colspan={colspan}
                                    dateTimeFormatter={dateTimeFormatter}
                                    isLastMeeting={meetingsArtifacts.length - 1 === index}
                                    vm={vm}
                                />;
                            })
                        }
                    </ListLayout>
                    {isEmpty && !isLoading &&
                        <div className='empty-list-message'>
                            {
                                intl.formatMessage({
                                    id: 'oe.artifacts.emptyListText',
                                    defaultMessage: 'No artifacts have been requested.',
                                    description: 'Artifacts list empty text'
                                })
                            }
                        </div>
                    }
                </div>
            </section>
            <RequestArtifactsPopover
                vm={requestArtifactsPopoverVM}
                intl={intl}
                isRequestArtifactsModalOpen={vm.isRequestArtifactsModalOpen}
                closePopover={() => vm.closeRequestArtifactsModal()}
            />
            <PublishArtifactPopover
                dateTimeFormatter={dateTimeFormatter}
                vm={publishArtifactPopoverVM}
                eventTimeZone={event.timeZone}
                intl={intl}
                onOpenKnovioPreviewLinkPopover={vm.onOpenKnovioPreviewLinkPopover}
            />
            <KnovioPreviewLinkPopover vm={vm?.getKnovioPreviewLinkPopoverVM()}/>
        </div>
    );
});

const ArtifactsContainer = (props: IProps) => {
    const { vm } = props;

    useEffect(() => {
        vm.initialize();
        return () => vm.cancelArtifactSubscriptions();
    }, []);

    return <ArtifactsPresentation vm={vm} />;
};


export { ArtifactsContainer as ArtifactsList, ArtifactsPresentation };
